import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { CalendarDays, Clock, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useSchools, SchoolSelect } from "../../components/SchoolSelect";
import { localName, localFullName } from "../../lib/localName";

const TODAY = new Date().toISOString().slice(0, 10);

export default function TripsPage() {
  const {  t , lang } = useI18n();
  const { schools, schoolId, setSchoolId } = useSchools();
  const [date, setDate] = useState(TODAY);
  const [trips, setTrips] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const load = () => { if (schoolId) api.get(`/trips?date=${date}&school_id=${schoolId}`).then(r => setTrips(r.data || [])); };
  useEffect(() => {
    if (!schoolId) return;
    load();
    api.get(`/routes?school_id=${schoolId}`).then(r => setRoutes(r.data || []));
    api.get(`/drivers?school_id=${schoolId}`).then(r => setDrivers(r.data || []));
  }, [date, schoolId]);

  const generate = async () => {
    try {
      const r = await api.post(`/trips/generate?date=${date}&school_id=${schoolId}`);
      toast.success(`${r.data.created} ${t("trips")}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const updateStatus = async (trip, patch) => {
    try {
      await api.put(`/trips/${trip.id}/status`, patch);
      toast.success(t("success"));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-3"><CalendarDays className="w-7 h-7 text-primary" /> {t("trips")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("delay_min")} · {t("status")}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <SchoolSelect schools={schools} value={schoolId} onChange={setSchoolId} testId="trips-school-select" />
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="trips-date" />
          <Button variant="outline" onClick={generate} data-testid="trips-generate-btn"><Clock className="w-4 h-4 me-2" />{t("generate_today")}</Button>
          <Button variant="ghost" size="icon" onClick={load} data-testid="trips-refresh"><RefreshCw className="w-4 h-4" /></Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {trips.map(t_ => {
          const route = routes.find(r => r.id === t_.route_id);
          const driver = drivers.find(d => d.id === t_.driver_id);
          return (
            <Card key={t_.id} className="p-5 border shadow-none" data-testid={`trip-${t_.id}`}>
              <div className="flex justify-between items-start gap-3 mb-3">
                <div>
                  <div className="font-semibold flex items-center gap-2">{route?.name || t_.route_id} <span className="text-xs bg-muted px-2 py-0.5 rounded">{t(t_.direction)}</span></div>
                  <div className="text-xs text-muted-foreground mt-1">{localFullName(driver, lang) || "—"} · {route?.start_time}</div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${t_.status === "completed" ? "bg-primary/10 text-primary" : t_.status === "started" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300" : "bg-muted text-muted-foreground"}`}>{t(t_.status)}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{t("status")}</Label>
                  <Select value={t_.status} onValueChange={v => updateStatus(t_, { status: v })}>
                    <SelectTrigger className="mt-1" data-testid={`trip-status-${t_.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="scheduled">{t("scheduled")}</SelectItem>
                      <SelectItem value="started">{t("started")}</SelectItem>
                      <SelectItem value="completed">{t("completed")}</SelectItem>
                      <SelectItem value="cancelled">{t("cancelled")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{t("delay_min")}</Label>
                  <Input type="number" defaultValue={t_.delay_min} className="mt-1" onBlur={e => updateStatus(t_, { delay_min: Number(e.target.value) || 0 })} data-testid={`trip-delay-${t_.id}`} />
                </div>
              </div>
            </Card>
          );
        })}
        {trips.length === 0 && <Card className="col-span-full p-12 text-center text-muted-foreground border-dashed">{t("no_trips_today")}</Card>}
      </div>
    </div>
  );
}
